
import { jitsiLocalStorage } from '@jitsi/js-utils';
import { PureComponent } from 'react';
import type { Dispatch } from 'redux';

import { getWindowWidth, isMobileBrowser } from '../../base/environment/utils';
import {
    getClientHeight,
    getClientWidth
} from '../../base/modal/components/functions.native';
import { getLocalParticipant, isLocalParticipantModerator } from '../../base/participants';
import { getBackendSafeRoomName } from '../../base/util';
import { pollsTabFocusedHandler } from '../../conference/actions';
import { arePollsDisabled } from '../../conference/functions.any';
import { screen } from '../../mobile/navigation/routes';
import { iAmVisitor, isVisitorsModeEnabled } from '../../visitors/functions';
import { sendMessage, setIsPollsTabFocused } from '../actions';
import { setVisitorTabFocused } from '../actions.any';
import {
    DESKTOP_SMALL_WIDTH_THRESHOLD,
    MOBILE_SMALL_WIDTH_THRESHOLD
} from '../constants';
import { splitMessagesByThread } from '../functions';

/**
 * The type of the React {@code Component} props of {@code AbstractChat}.
 */
export type Props = {

    /**
     * Whether the chat is opened in a modal or not (computed based on window width).
     */
    _isModal: boolean,

    /**
     * True if the chat window should be rendered.
     */
    _isOpen: boolean,

    /**
     * All the chat messages in the conference.
     */
    _messages: Array<Object>,

    /**
     * Function to send a text message.
     *
     * @protected
     */
    _onSendMessage: Function,

    _finalAccessToken: any,

    _mhClientID: any,

    _meetingID: any,

    _domain: any,

    _email: any,

    _config: any,

    /**
     * Function to toggle the chat window.
     */
    _onToggleChat: Function,

    /**
     * Whether or not to block chat access with a nickname input form.
     */
    _showNamePrompt: boolean,

    /**
     * The Redux dispatch function.
     */
    dispatch: Dispatch<any>,

    /**
     * Function to be used to translate i18n labels.
     */
    t: Function,


};

/**
 * Implements an abstract chat panel.
 */
export default class AbstractChat<P: Props> extends PureComponent<P> {
    /**
     * Initializes a new {@code AbstractChat} instance.
     *
     * @param {Props} props - The React {@code Component} props to initialize
     * the new {@code AbstractChat} instance with.
     */
    constructor(props: P) {
        super(props);

        // Bind event handlers so they are only bound once per instance.
        this._onSendMessage = this._onSendMessage.bind(this);
        this._onToggleChatTab = this._onToggleChatTab.bind(this);
        this._onTogglePollsTab = this._onTogglePollsTab.bind(this);
    }

    _onSendMessage: (string) => void;

    /**
     * Sends a text message.
     *
     * @private
     * @param {string} text - The text message to be sent.
     * @returns {void}
     * @type {Function}
     */
    _onSendMessage(text: string) {
        // Main-room participants viewing the visitor chat thread post directly into it. Their
        // messages are marked with the visitor channel so every recipient routes them to the
        // visitor chat panel and keeps them out of the main-room chat panel.
        const channel = !this.props._iAmVisitor && this.props._isVisitorTabFocused ? 'visitor' : undefined;

        this.props.dispatch(sendMessage(text, false, channel));
    }
    _onToggleChatTab: () => void;

    /**
     * Display the Chat tab.
     *
     * @private
     * @returns {void}
     */
    _onToggleChatTab() {
        this.props.dispatch(setIsPollsTabFocused(false));
        this.props.dispatch(pollsTabFocusedHandler(false));

        // Return to main-room chat panel.
        this.props.dispatch(setVisitorTabFocused(false));
    }

    _onTogglePollsTab: () => void;

    /**
     * Display the Polls tab.
     *
     * @private
     * @returns {void}
     */
    _onTogglePollsTab() {
        this.props.dispatch(setIsPollsTabFocused(true));
        this.props.dispatch(pollsTabFocusedHandler(true));

        // Return to main-room polls panel.
        this.props.dispatch(setVisitorTabFocused(false));
    }
}

/**
 * Maps (parts of) the redux state to {@link Chat} React {@code Component}
 * props.
 *
 * @param {Object} state - The redux store/state.
 * @private
 * @returns {{
 *     _isOpen: boolean,
 *     _messages: Array<Object>,
 *     _showNamePrompt: boolean
 * }}
 */
export function _mapStateToProps(state: Object) {
    const {
        isOpen,
        messages,
        isPollsTabFocused,
        isVisitorTabFocused,
        nbUnreadPolls,
        isPollsOpen
    } = state['features/chat'];
    const _localParticipant = getLocalParticipant(state);
    const nbUnreadMessages = state['features/chat']?.nbUnreadMessages || 0;
    const { accessToken } = state['features/base/jwt'];
    const userAccessToken = jitsiLocalStorage.getItem('accessToken');
    const finalAccessToken = accessToken ?? userAccessToken;
    const { MH_CLIENT_ID } = state['features/base/config'];
    const config = state['features/base/config'];
    const MeetingID = getBackendSafeRoomName(state['features/base/conference'].room);
    const domain = state['features/base/config'].hosts.domain;
    const { email } = state['features/base/settings'];

    const clientHeight = getClientHeight(state);
    const clientWidth = getClientWidth(state);

    const initialRouteName = isPollsTabFocused
        ? screen.conference.chatandpolls.tab.polls
        : screen.conference.chatandpolls.tab.chat;

    // Separate messages by chat thread so the UI can show distinct panels. Memoized by the messages
    // array reference, so this is O(n) only when the message list actually changes and returns
    // stable references otherwise (keeps the message panels from re-rendering on unrelated updates).
    const { mainMessages, visitorMessages } = splitMessagesByThread(messages);

    const _isVisitor = iAmVisitor(state);

    // The visitor thread only exists while the room is actually running visitor
    // mode. Gating the focus flag here rather than at each use keeps the panel,
    // the pill state and the outgoing message channel from disagreeing: when a
    // moderator turns visitor mode off while the visitor thread is open, the
    // stale flag would otherwise leave a main-room user reading (and posting
    // into) a thread whose switch has just disappeared.
    const _visitorsModeEnabled = isVisitorsModeEnabled(state);

    return {
        _isModal: isMobileBrowser()
            ? getWindowWidth() <= MOBILE_SMALL_WIDTH_THRESHOLD
            : getWindowWidth() <= DESKTOP_SMALL_WIDTH_THRESHOLD,
        _isOpen: isOpen,
        _isPollsOpen: isPollsOpen,
        _isModerator: isLocalParticipantModerator(state),

        _isPollsEnabled: !arePollsDisabled(state),

        // All messages kept for backward-compat (reactions etc.).
        _messages: messages,

        // Filtered lists for the separated panels.
        _mainMessages: mainMessages,
        _visitorMessages: visitorMessages,

        // Whether the local user is currently a visitor.
        _iAmVisitor: _isVisitor,

        // Whether the visitor content panel (visitor chat / visitor polls) is
        // the active view for non-visitor participants.
        _isVisitorTabFocused: isVisitorTabFocused && _visitorsModeEnabled,

        // Whether the room is running visitor mode at all, i.e. whether a
        // visitor thread is worth offering.
        _visitorsModeEnabled,

        _showNamePrompt: !_localParticipant.name,
        _isPrivateChatDisabled: state['features/chat']?.isPrivateChatDisabled || false,
        _isPollsTabFocused: isPollsTabFocused,
        _nbUnreadMessages: nbUnreadMessages,
        _nbUnreadPolls: nbUnreadPolls,
        _clientHeight: clientHeight,
        _clientWidth: clientWidth,
        _initialRouteName: initialRouteName,
        _interfaceConfig: state['features/base/config']?.interfaceConfig,

        // For Document Picking
        _finalAccessToken: finalAccessToken,
        _mhClientID: MH_CLIENT_ID,
        _meetingID: MeetingID,
        _domain: domain,
        _email: email,
        _config: config
    };
}
